import { useState } from "react"
import { Loader2, Copy, Check } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

type OrgRole = "admin" | "member" | "viewer"

interface CreateOrgDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (organizationName: string, adminEmail: string, role: OrgRole) => Promise<{ inviteUrl: string }>
  defaultOrgName?: string
  defaultEmail?: string
  title?: string
  description?: string
}

export function CreateOrgDialog({
  open,
  onOpenChange,
  onSubmit,
  defaultOrgName = "",
  defaultEmail = "",
  title = "Create Organisation",
  description = "Create a new organisation and send an admin invitation.",
}: CreateOrgDialogProps) {
  const [orgName, setOrgName] = useState(defaultOrgName)
  const [email, setEmail] = useState(defaultEmail)
  const [role, setRole] = useState<OrgRole>("admin")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [inviteUrl, setInviteUrl] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!orgName.trim() || !email.trim()) return

    try {
      setIsSubmitting(true)
      setError(null)
      const result = await onSubmit(orgName.trim(), email.trim(), role)
      setInviteUrl(result.inviteUrl)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create organisation")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCopy = async () => {
    if (!inviteUrl) return
    await navigator.clipboard.writeText(inviteUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleClose = (open: boolean) => {
    if (!open) {
      // Reset state when closing
      setOrgName(defaultOrgName)
      setEmail(defaultEmail)
      setRole("admin")
      setInviteUrl(null)
      setCopied(false)
      setError(null)
    }
    onOpenChange(open)
  }

  // Success state — show invite URL
  if (inviteUrl) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Organisation Created</DialogTitle>
            <DialogDescription>
              The organisation has been created and an admin invitation is ready.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-meta">Invite URL</label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={inviteUrl}
                  readOnly
                  className="font-mono text-xs"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => void handleCopy()}
                  aria-label="Copy invite URL"
                >
                  {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => handleClose(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  // Form state
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          <div>
            <label htmlFor="org-name" className="text-sm font-medium">
              Organisation Name
            </label>
            <Input
              id="org-name"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              placeholder="Acme Corp"
              required
              className="mt-1"
            />
          </div>
          <div>
            <label htmlFor="admin-email" className="text-sm font-medium">
              User Email
            </label>
            <Input
              id="admin-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@acme.com"
              required
              className="mt-1"
            />
          </div>
          <div>
            <label htmlFor="user-role" className="text-sm font-medium">
              Role
            </label>
            <Select value={role} onValueChange={(v) => setRole(v as OrgRole)}>
              <SelectTrigger id="user-role" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin — can invite users and manage settings</SelectItem>
                <SelectItem value="member">Member — can create and edit bids</SelectItem>
                <SelectItem value="viewer">Viewer — read-only access</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => handleClose(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || !orgName.trim() || !email.trim()}>
              {isSubmitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
